import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repositoryService, bookNames } from './repository-service';

interface BookGroup {
  label: string;
  books: string[];
}

const OT_GROUPS: BookGroup[] = [
  { label: 'Law', books: ['Gen', 'Exod', 'Lev', 'Num', 'Deut'] },
  { label: 'History', books: ['Josh', 'Judg', 'Ruth', '1Sam', '2Sam', '1Kgs', '2Kgs', '1Chr', '2Chr', 'Ezra', 'Neh', 'Esth'] },
  { label: 'Poetry', books: ['Job', 'Ps', 'Prov', 'Eccl', 'Song'] },
  { label: 'Major Prophets', books: ['Isa', 'Jer', 'Lam', 'Ezek', 'Dan'] },
  { label: 'Minor Prophets', books: ['Hos', 'Joel', 'Amos', 'Obad', 'Jonah', 'Mic', 'Nah', 'Hab', 'Zeph', 'Hag', 'Zech', 'Mal'] },
];

const NT_GROUPS: BookGroup[] = [
  { label: 'Gospels', books: ['Matt', 'Mark', 'Luke', 'John'] },
  { label: 'History', books: ['Acts'] },
  { label: "Paul's Letters", books: ['Rom', '1Cor', '2Cor', 'Gal', 'Eph', 'Phil', 'Col', '1Thess', '2Thess', '1Tim', '2Tim', 'Titus', 'Phlm'] },
  { label: 'General Letters', books: ['Heb', 'Jas', '1Pet', '2Pet', '1John', '2John', '3John', 'Jude'] },
  { label: 'Prophecy', books: ['Rev'] },
];

@customElement('app-book-navigator')
export class BookNavigator extends LitElement {
  createRenderRoot = () => this

  @property({ type: Boolean }) open = false
  @property() step: 'books' | 'chapters' = 'books'
  @property() selectedBook = ''
  @property({ type: Array }) versions: string[] = []
  @property({ type: Number }) chapterCount = 0

  private _getAvailableBooks(): Set<string> {
    return repositoryService.getUnifiedBooksList(this.versions)
  }

  private async _getChapterCount(book: string): Promise<number> {
    for (const version of this.versions) {
      const items = await repositoryService.getBookItems(version, book)
      const count = items.filter(i => i.type === 'chapter').length
      if (count > 0) return count
    }
    return 0
  }

  show() {
    this.open = true
    this.step = 'books'
    this.selectedBook = ''
  }

  close() {
    this.open = false
  }

  private async _selectBook(book: string) {
    this.selectedBook = book
    this.chapterCount = await this._getChapterCount(book)
    this.step = 'chapters'
  }

  private _selectChapter(chapter: number) {
    const osis = `${this.selectedBook}.${chapter}.1`
    const book = this.selectedBook
    this.close()
    // Dispatch after close so the overlay DOM is gone and scroll can settle
    requestAnimationFrame(() => {
      this.dispatchEvent(new CustomEvent('navigate-to', {
        bubbles: true, composed: true,
        detail: { book, osis }
      }))
    })
  }

  private _goBack() {
    this.step = 'books'
    this.selectedBook = ''
  }

  private _renderBookGroups(groups: BookGroup[], available: Set<string>, testament: string) {
    const visibleGroups = groups.filter(g => g.books.some(b => available.has(b)))
    if (!visibleGroups.length) return ''

    return html`
      <div class="nav-testament-label">${testament}</div>
      ${visibleGroups.map(group => {
        const visibleBooks = group.books.filter(b => available.has(b))
        if (!visibleBooks.length) return ''
        return html`
          <div class="nav-book-group">
            <div class="nav-group-label">${group.label}</div>
            <div class="nav-book-grid">
              ${visibleBooks.map(book => html`
                <button class="nav-book-btn" @click=${() => this._selectBook(book)}>
                  ${bookNames[book] || book}
                </button>
              `)}
            </div>
          </div>
        `
      })}
    `
  }

  render() {
    if (!this.open) return html``

    const available = this._getAvailableBooks()

    if (this.step === 'chapters') {
      const chapterCount = this.chapterCount
      const bookName = bookNames[this.selectedBook] || this.selectedBook
      return html`
        <div class="nav-overlay" @click=${(e: Event) => { if ((e.target as Element).classList.contains('nav-overlay')) this.close() }}>
          <div class="nav-content">
            <div class="nav-header">
              <button class="nav-back-btn" @click=${() => this._goBack()}>
                Back
              </button>
              <h2 class="nav-title">${bookName}</h2>
              <button class="nav-close-btn" @click=${() => this.close()}>
                Close
              </button>
            </div>
            <div class="nav-subtitle">Select a chapter</div>
            <div class="nav-chapter-grid">
              ${Array.from({ length: chapterCount }, (_, i) => i + 1).map(ch => html`
                <button class="nav-chapter-btn" @click=${() => this._selectChapter(ch)}>
                  ${ch}
                </button>
              `)}
            </div>
          </div>
        </div>
      `
    }

    return html`
      <div class="nav-overlay" @click=${(e: Event) => { if ((e.target as Element).classList.contains('nav-overlay')) this.close() }}>
        <div class="nav-content">
          <div class="nav-header">
            <div></div>
            <h2 class="nav-title">Select a Book</h2>
            <button class="nav-close-btn" @click=${() => this.close()}>
              Close
            </button>
          </div>
          <div class="nav-body">
            ${this._renderBookGroups(OT_GROUPS, available, 'Old Testament')}
            ${this._renderBookGroups(NT_GROUPS, available, 'New Testament')}
          </div>
        </div>
      </div>
    `
  }
}
